import React from 'react';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, formatDate } from '../regureStyles';
import usePgmRateTablesFunctions from './usePgmRateTablesFunctions';

const WAITING_PERIODS = [14, 30, 60, 90];
const COVERAGE_PERCENTS = [80, 90];

const rateInputStyle = {
  width: 90,
  padding: '6px 9px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 6,
  fontSize: 12,
  color: S.textPrimary,
  outline: 'none',
  textAlign: 'right',
  boxSizing: 'border-box',
  fontVariantNumeric: 'tabular-nums',
};

const constInputStyle = {
  ...rateInputStyle,
  width: 130,
  textAlign: 'left',
};

const labelStyle = { fontSize: 11, fontWeight: 600, color: S.textSecondary, textTransform: 'uppercase', letterSpacing: 0.6 };

const RateCell = ({ row, updateRate, t }) => {
  if (!row) return <td style={{ padding: '8px 12px', color: S.textMuted, fontSize: 12 }}>—</td>;
  const provisional = !row.confirmed;
  return (
    <td style={{ padding: '6px 12px', background: provisional ? 'rgba(245,158,11,0.06)' : 'transparent' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <input
            type='number'
            step='0.001'
            min='0'
            value={row.rate ?? ''}
            onChange={e => updateRate(row.waitingPeriod, row.coveragePercent, 'rate', e.target.value)}
            style={rateInputStyle}
          />
          <span style={{ fontSize: 12, color: S.textMuted }}>%</span>
        </div>
        <label
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
          title={provisional ? t('rateTables.provisionalTooltip') : t('rateTables.confirmedTooltip')}
        >
          <input
            type='checkbox'
            checked={!!row.confirmed}
            onChange={e => updateRate(row.waitingPeriod, row.coveragePercent, 'confirmed', e.target.checked)}
          />
          <span style={{ fontSize: 10, fontWeight: 600, color: provisional ? S.warning : S.success, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {provisional ? t('rateTables.provisional') : t('rateTables.confirmed')}
          </span>
        </label>
      </div>
    </td>
  );
};

const PgmRatesEditor = () => {
  const { t } = useTranslation();
  const {
    table,
    rates,
    constants,
    updateRate, updateConstant,
    offeredWaits, toggleOfferedWait,
    loading, saving, dirty, error, toast,
    save, reload,
  } = usePgmRateTablesFunctions();

  const findRow = (wp, cp) => rates.find(r =>
    Number(r.waitingPeriod) === wp && Number(r.coveragePercent) === cp
  );
  const hasProvisional = rates.some(r => !r.confirmed);

  const constantsFields = [
    { key: 'salaryCap',           label: t('rateTables.constSalaryCap'),         suffix: 'CHF',                       step: '1000', type: 'number' },
    { key: 'maxBenefitDays',      label: t('rateTables.constMaxBenefitDays'),    suffix: t('offers.common.daysUnit'), step: '1',    type: 'number' },
    { key: 'avsBenefitDays',      label: t('rateTables.constAvsBenefitDays'),    suffix: t('offers.common.daysUnit'), step: '1',    type: 'number' },
    { key: 'minimumPremium',      label: t('rateTables.constMinimumPremium'),    suffix: 'CHF',                       step: '1',    type: 'number' },
    { key: 'defaultNogaCode',     label: t('rateTables.constDefaultNoga'),       suffix: '',                          step: '1',    type: 'text'   },
    { key: 'coverageDescription', label: t('rateTables.constCoverageDescription'), suffix: '',                        step: '',     type: 'text'   },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
        <div>
          {table?.effectiveDate && (
            <div style={{ fontSize: 11, color: S.textMuted }}>
              {t('rateTables.effective', { date: formatDate(table.effectiveDate) })}
            </div>
          )}
          <div style={{ fontSize: 12, color: S.textSecondary, marginTop: 4, fontStyle: 'italic' }}>
            {t('rateTables.pgmSubtitle')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 12, color: S.warning, fontWeight: 600 }}>{t('rateTables.unsavedChanges')}</span>}
          <button
            onClick={reload}
            disabled={loading || saving}
            style={{
              padding: '8px 14px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8,
              fontSize: 13, fontWeight: 600, color: S.textSecondary, cursor: 'pointer',
              opacity: (loading || saving) ? 0.6 : 1,
            }}
          >
            {t('rateTables.reset')}
          </button>
          <BtnPrimary onClick={save} disabled={!dirty || saving} style={{ opacity: (!dirty || saving) ? 0.6 : 1 }}>
            <Icon name='check' color='#fff' size={15} /> {saving ? t('rateTables.saving') : t('rateTables.saveChanges')}
          </BtnPrimary>
        </div>
      </div>

      {hasProvisional && (
        <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.1)', border: `1px solid ${S.warning}50`, borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 14 }}>
          <Icon name='alert-triangle' color={S.warning} size={14} /> {t('rateTables.provisionalWarning')}
        </div>
      )}

      {error && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 14 }}>
          {error}
        </div>
      )}
      {toast && (
        <div style={{ padding: '10px 14px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, fontSize: 13, color: '#166534', marginBottom: 14 }}>
          {toast}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{t('rateTables.loadingPgm')}</div>
      ) : (
        <>
          <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: S.bgCard }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${S.borderLight}` }}>
                    {t('rateTables.waitingHeader')}
                  </th>
                  {COVERAGE_PERCENTS.map(cp => (
                    <th key={cp} style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${S.borderLight}` }}>
                      {t('rateTables.coveragePgmPercent', { percent: cp })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {WAITING_PERIODS.map(wp => {
                  const offered = offeredWaits.includes(Number(wp));
                  return (
                  <tr key={wp} style={{ borderBottom: `1px solid ${S.borderLight}`, opacity: offered ? 1 : 0.5 }}>
                    <td style={{ padding: '8px 14px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                      {/* The signed tariff prices this row; whether it is on
                          sale is the tenant's call. Unticking hides it from
                          the PGM quote form and the engine refuses it —
                          the priced row itself is kept, so it is one tick
                          to put back. */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input
                          type='checkbox'
                          checked={offered}
                          onChange={() => toggleOfferedWait(wp)}
                          style={{ cursor: 'pointer' }}
                        />
                        {t('rateTables.daysJours', { days: wp })}
                      </label>
                    </td>
                    {COVERAGE_PERCENTS.map(cp => (
                      <RateCell key={cp} row={findRow(wp, cp)} updateRate={updateRate} t={t} />
                    ))}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: S.textPrimary, marginBottom: 12 }}>{t('rateTables.productConstants')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
              {constantsFields.map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <input
                      type={f.type}
                      step={f.step || undefined}
                      value={constants[f.key] ?? ''}
                      onChange={e => updateConstant(f.key, e.target.value)}
                      style={f.key === 'coverageDescription' ? { ...constInputStyle, width: 260 } : constInputStyle}
                    />
                    {f.suffix && <span style={{ fontSize: 12, color: S.textMuted }}>{f.suffix}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 14, fontSize: 11, color: S.textMuted }}>
            {t('rateTables.offeredWaitsNote', {
              defaultValue: 'Untick a waiting period to stop offering it. The rate stays in the table and the choice disappears from the quote form — quotes already written keep the period they were priced on.',
            })}
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: S.textMuted }}>
            {t('rateTables.rateChangesNote')}
          </div>
        </>
      )}
    </>
  );
};

export default PgmRatesEditor;
